/**
 * Dezenter Partikel-Hintergrund fuer die Anmeldeseite.
 *
 * Punkte mit feinen Verbindungslinien zwischen nahen Nachbarn. Die Assoziation
 * ist Vernetzung und Unternehmenswissen -- nicht Science-Fiction.
 *
 * GESTALTUNGSREGELN (Auftrag Abschnitt 5)
 *   wenige Partikel, langsame Bewegung, niedrige Deckkraft, Grautoene,
 *   greenius-Gruen nur als sparsamer Akzent. Kein Glow, kein Neon.
 *
 * TECHNIK
 *   Reines Canvas, keine Animationsbibliothek. Die Schleife haelt an, sobald
 *   der Tab in den Hintergrund geht. `prefers-reduced-motion` erzeugt ein
 *   einzelnes statisches Bild statt einer Animation.
 *
 * ── VOM NUTZER GEZOGENE VERBINDUNGEN (PROD LOGIN 001 §3) ─────────────
 *
 * Einen Punkt greifen, zu einem zweiten ziehen, loslassen -- daraus wird
 * eine Verbindung, die den Rest der Sitzung sichtbar bleibt. Beim Ziehen
 * laeuft eine Linie vom gegriffenen Punkt zum Zeiger mit.
 *
 * SIE LEBT AUSSCHLIESSLICH IM SPEICHER. Kein Backend, keine Datenbank,
 * keine Benutzerpraeferenz, kein `localStorage`: ein Neuladen setzt sie
 * zurueck, und das ist die Zusage, nicht ein Mangel. Verbunden werden
 * PUNKTE und nicht Koordinaten -- die Linie folgt ihnen, wenn sie
 * weiterwandern.
 *
 * ── EIN KLICK SETZT EINEN PUNKT (INTERACTION EXTENSION 001) ──────────
 *
 * Auf freier Flaeche entsteht aus einem Klick ein neuer Punkt. Er
 * verhaelt sich wie die automatischen: er wandert langsam, er laesst
 * sich greifen, er verbindet sich -- mit automatischen und mit anderen
 * gesetzten. Verbunden werden PUNKTE, nicht Arten von Punkten; die
 * vier Kombinationen aus §3 brauchen deshalb keinen Sonderfall.
 *
 * KLICK UND ZUG SCHLIESSEN SICH AUS. Wer an einem Punkt aufsetzt,
 * zieht eine Verbindung und setzt nichts; wer auf leerer Flaeche
 * aufsetzt und sich weiter als vier Bildpunkte bewegt, hat gezogen und
 * setzt auch nichts.
 *
 * DAS FORMULAR BLEIBT UNBERUEHRT. Gelauscht wird am Hintergrund, und
 * jeder Zeiger, der auf dem Anmeldefeld, dem Sprachschalter oder einem
 * anderen Bedienelement beginnt, wird durchgelassen. Das Canvas selbst
 * behaelt `pointer-events: none`; es ist Darstellung und kein
 * Bedienelement. Passwortmanager und Autofuellen sehen deshalb genau
 * dieselben Felder wie vorher.
 */
import { useEffect, useRef } from "react";
import "./ParticleNetwork.css";

interface Particle {
  x: number; y: number; vx: number; vy: number; r: number; accent: boolean;
  /** Von einem Menschen gesetzt (INTERACTION EXTENSION 001). */
  manuell?: boolean;
}

/** Ein Partikel je rund 26.000 Bildpunkten, hart gedeckelt. */
const DENSITY = 26_000;
const MAX_PARTICLES = 90;
const LINK_DISTANCE = 150;
const POINTER_RADIUS = 170;
/*
 * Wie nah der Zeiger an einem Punkt sein muss, um ihn zu greifen.
 *
 * Die Punkte haben einen Radius von knapp zwei Bildpunkten -- genau auf
 * ihnen zu treffen waere eine Geduldsprobe. 18 Bildpunkte sind grosszuegig
 * genug fuer die Maus und klein genug, dass bei 90 Punkten auf einem
 * Bildschirm nicht dauernd einer "unter" dem Zeiger liegt.
 */
const GRAB_RADIUS = 18;
/*
 * ── KLICK ODER ZUG (EXTENSION §1/§6) ────────────────────────────────
 *
 * Ein Klick setzt einen Punkt, ein Zug verbindet -- und beides darf
 * sich nicht vermischen. Unterschieden wird an der Strecke zwischen
 * Aufsetzen und Loslassen: wer weniger als vier Bildpunkte wandert,
 * hat geklickt.
 *
 * Vier und nicht null: eine Maus zittert, ein Finger rollt ab. Null
 * hiesse, dass auf einem Trackpad fast nie ein Klick ankommt.
 */
const DRAG_SCHWELLE = 4;
/*
 * ── WIE VIELE PUNKTE EIN MENSCH SETZEN DARF (EXTENSION §5) ──────────
 *
 * Die Verbindungssuche je Bild ist quadratisch: bei n Punkten sind es
 * n*(n-1)/2 Abstandsrechnungen. Automatisch entstehen hoechstens 90 --
 * das sind 4.005. Mit 60 gesetzten kommt man auf 150 Punkte und
 * 11.175 Rechnungen je Bild, also knapp das Dreifache. Gemessen bleibt
 * das Zeichnen damit deutlich unter einem Bild bei 60 Hz.
 *
 * 60 ist pragmatisch gewaehlt: genug, um ein Netz zu legen, zu wenig,
 * um die Seite lahmzulegen. Es gibt dafuer bewusst keine Anzeige --
 * wer die Grenze erreicht, hat etwas anderes im Sinn als sich
 * anzumelden.
 */
const MAX_MANUELL = 60;

export function ParticleNetwork(): React.JSX.Element {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return;

    /*
     * Gelauscht wird am Hintergrund und nicht am Canvas: das traegt
     * `pointer-events: none` und soll es behalten -- es ist
     * Darstellung. Die Flaeche ist das Elternteil, also `.login`.
     */
    const flaeche = canvas.parentElement;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let particles: Particle[] = [];
    let width = 0, height = 0, dpr = 1;
    let frame = 0;
    const pointer = { x: -9999, y: -9999, active: false };
    /*
     * Die vom Nutzer gezogenen Verbindungen -- Paare von PUNKTEN, nicht
     * von Koordinaten. Nur im Speicher dieser Sitzung (§3).
     */
    const links: { a: Particle; b: Particle }[] = [];
    /* Der gegriffene Punkt, solange gezogen wird. */
    let greift: Particle | null = null;
    let zeigerId = -1;
    /*
     * Wo der Zeiger aufgesetzt hat -- daran entscheidet sich beim
     * Loslassen, ob es ein Klick war oder ein Zug (EXTENSION §1).
     */
    let abX = 0, abY = 0;
    /* Wie viele Punkte ein Mensch in dieser Sitzung gesetzt hat. */
    let gesetzt = 0;
    /* Ob gerade ein Punkt unter dem Zeiger liegt -- nur fuer den Cursor. */
    let greifbar = false;

    function build(): void {
      const rect = canvas!.getBoundingClientRect();
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = rect.width;
      height = rect.height;
      canvas!.width = Math.round(width * dpr);
      canvas!.height = Math.round(height * dpr);
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);

      /*
       * Ein Neuaufbau wuerfelt die Punkte neu -- die Verbindungen des
       * Nutzers zeigten danach auf Punkte, die es nicht mehr gibt.
       * Sie gehen deshalb mit. Das betrifft nur Fenstergroessen-
       * aenderungen; ein blosses Weiterlaufen laesst sie stehen.
       */
      links.length = 0;
      greift = null;
      /* Die gesetzten Punkte entstehen in `particles` und werden hier
         mit neu gewuerfelt -- die Zaehlung muss deshalb mit. */
      gesetzt = 0;

      const count = Math.min(MAX_PARTICLES, Math.round((width * height) / DENSITY));
      particles = Array.from({ length: count }, (_, i) => ({
        x: Math.random() * width,
        y: Math.random() * height,
        // Sehr langsam: rund 6 Bildpunkte je Sekunde.
        vx: (Math.random() - 0.5) * 0.11,
        vy: (Math.random() - 0.5) * 0.11,
        r: 0.9 + Math.random() * 0.9,
        // Nur etwa jeder zehnte Punkt traegt das Markengruen.
        accent: i % 10 === 0,
      }));
    }

    function draw(): void {
      ctx!.clearRect(0, 0, width, height);

      // Verbindungslinien zuerst, damit die Punkte darueber liegen.
      for (let i = 0; i < particles.length; i++) {
        const a = particles[i]!;
        for (let j = i + 1; j < particles.length; j++) {
          const b = particles[j]!;
          const dx = a.x - b.x, dy = a.y - b.y;
          const d2 = dx * dx + dy * dy;
          if (d2 > LINK_DISTANCE * LINK_DISTANCE) continue;
          const t = 1 - Math.sqrt(d2) / LINK_DISTANCE;
          /*
           * §2: etwas besser lesbar als die 0.14 von vorher -- aber
           * deutlich schwaecher als die Punkte (0.48). Die Linien sind
           * der Zusammenhang, die Punkte sind die Sache.
           */
          ctx!.strokeStyle = `rgba(168, 176, 162, ${(t * 0.20).toFixed(3)})`;
          ctx!.lineWidth = 0.6;
          ctx!.beginPath();
          ctx!.moveTo(a.x, a.y);
          ctx!.lineTo(b.x, b.y);
          ctx!.stroke();
        }
      }

      /*
       * ── §5 · DIE VOM NUTZER GEZOGENEN VERBINDUNGEN ───────────────
       *
       * Heller als die automatischen Linien (0.20 Grau) und deutlich
       * schwaecher als Logo und Anmeldefeld. Sie tragen das
       * Markengruen, weil sie eine ENTSCHEIDUNG sind und nicht blosse
       * Naehe -- dieselbe Sprache, in der das Gruen in HOMEBASE
       * markiert, ohne zu faerben. Keine neue Farblogik, keine
       * grellen Toene, kein Glow.
       */
      for (const l of links) {
        ctx!.strokeStyle = "rgba(149, 194, 61, 0.30)";
        ctx!.lineWidth = 0.9;
        ctx!.beginPath();
        ctx!.moveTo(l.a.x, l.a.y);
        ctx!.lineTo(l.b.x, l.b.y);
        ctx!.stroke();
      }

      /*
       * Die Linie, die beim Ziehen mitlaeuft -- vom gegriffenen Punkt
       * zum Zeiger. Sie ist gestrichelt: so ist auf den ersten Blick
       * zu sehen, dass sie noch keine Verbindung IST.
       */
      if (greift !== null && pointer.active) {
        ctx!.save();
        ctx!.setLineDash([3, 3]);
        ctx!.strokeStyle = "rgba(149, 194, 61, 0.34)";
        ctx!.lineWidth = 0.9;
        ctx!.beginPath();
        ctx!.moveTo(greift.x, greift.y);
        ctx!.lineTo(pointer.x, pointer.y);
        ctx!.stroke();
        ctx!.restore();
        /* Der gegriffene Punkt selbst tritt hervor, damit sichtbar
           bleibt, WORAN die Linie haengt. */
        ctx!.fillStyle = "rgba(149, 194, 61, 0.7)";
        ctx!.beginPath();
        ctx!.arc(greift.x, greift.y, greift.r + 1.6, 0, Math.PI * 2);
        ctx!.fill();
      }

      // Zeigerreaktion: nur Linien, kein Anziehen der Punkte. Zurueckhaltend.
      if (pointer.active) {
        for (const p of particles) {
          const dx = p.x - pointer.x, dy = p.y - pointer.y;
          const d = Math.hypot(dx, dy);
          if (d > POINTER_RADIUS) continue;
          const t = 1 - d / POINTER_RADIUS;
          ctx!.strokeStyle = `rgba(149, 194, 61, ${(t * 0.16).toFixed(3)})`;
          ctx!.lineWidth = 0.6;
          ctx!.beginPath();
          ctx!.moveTo(p.x, p.y);
          ctx!.lineTo(pointer.x, pointer.y);
          ctx!.stroke();
        }
      }

      /*
       * ── §2 · DIE PUNKTE DEUTLICHER ───────────────────────────────
       *
       * 0.26 auf 0.48 und 0.34 auf 0.55. Der Farbton bleibt: ein
       * helles Graugruen (186, 193, 180) und das Markengruen, beide
       * auf einem Grund um #2A2D27. Das ist klar erkennbar und
       * weiterhin kein Weiss -- reines Weiss waere 255 bei voller
       * Deckkraft, hier steht nicht einmal die Haelfte.
       *
       * Logo und Anmeldefeld bleiben dominant: sie tragen 0.9 bis 1.0
       * und eine eigene Flaeche.
       */
      for (const p of particles) {
        ctx!.fillStyle = p.accent ? "rgba(149, 194, 61, 0.55)" : "rgba(186, 193, 180, 0.48)";
        ctx!.beginPath();
        ctx!.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx!.fill();
      }
    }

    function step(): void {
      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        // Weicher Rand: Punkte kehren um, statt zu springen.
        if (p.x < 0 || p.x > width) p.vx = -p.vx;
        if (p.y < 0 || p.y > height) p.vy = -p.vy;
      }
      draw();
      frame = requestAnimationFrame(step);
    }

    function start(): void {
      cancelAnimationFrame(frame);
      if (reduced) draw();
      else frame = requestAnimationFrame(step);
    }

    function onResize(): void { build(); start(); }
    function onVisibility(): void {
      if (document.hidden) cancelAnimationFrame(frame);
      else start();
    }
    function onPointer(e: PointerEvent): void {
      const r = canvas!.getBoundingClientRect();
      pointer.x = e.clientX - r.left;
      pointer.y = e.clientY - r.top;
      pointer.active = true;
      /*
       * Der Cursor sagt, dass hier etwas zu greifen ist -- aber nur,
       * wenn wirklich ein Punkt in Reichweite liegt. Die Klasse wird
       * NUR BEI EINEM WECHSEL gesetzt: ein DOM-Schreibvorgang je
       * Zustandsaenderung statt einer je Bewegung oder je Bild.
       */
      const nah = greift === null ? naechster(pointer.x, pointer.y) !== null : true;
      if (nah !== greifbar) {
        greifbar = nah;
        flaeche?.classList.toggle("login--greifbar", nah);
      }
      /* Ohne Animation wird nur gezeichnet, wenn sich etwas aendert. */
      if (reduced) draw();
    }
    function onLeave(): void {
      pointer.active = false;
      if (greifbar) { greifbar = false; flaeche?.classList.remove("login--greifbar"); }
      if (reduced) draw();
    }

    /** Der Punkt in Greifweite -- oder `null`. */
    function naechster(x: number, y: number): Particle | null {
      let beste: Particle | null = null;
      let bestD = GRAB_RADIUS;
      for (const p of particles) {
        const d = Math.hypot(p.x - x, p.y - y);
        if (d < bestD) { bestD = d; beste = p; }
      }
      return beste;
    }

    /*
     * ── WANN EIN ZUG NICHT BEGINNT (§4) ──────────────────────────────
     *
     * Jeder Zeiger, der auf einem Bedienelement aufsetzt, wird
     * durchgelassen: das Anmeldefeld, der Knopf, der Sprachschalter,
     * eine Beschriftung, ein Verweis. Gezogen wird ausschliesslich im
     * Hintergrund.
     *
     * Geprueft wird am ZIEL des Ereignisses und nicht an Koordinaten --
     * ein Rechteck um das Formular waere beim naechsten Umbau falsch,
     * und der Passwortmanager setzt seine eigenen Felder ohnehin dort
     * ein, wo er will.
     */
    const UI = "input, button, select, textarea, label, a, [role=button], "
      + ".login__panel, .login__lang, .login__foot";

    function onDown(e: PointerEvent): void {
      if (e.button !== 0 && e.pointerType === "mouse") return;
      const ziel = e.target as Element | null;
      if (ziel?.closest(UI)) return;
      const r = canvas!.getBoundingClientRect();
      const x = e.clientX - r.left, y = e.clientY - r.top;
      /*
       * HIER WURDE FRUEHER ABGEBROCHEN, wenn kein Punkt in Reichweite
       * lag. Seit EXTENSION 001 ist auch der leere Hintergrund ein
       * Anfang: ein Klick darauf setzt einen Punkt.
       *
       * Gemerkt wird in beiden Faellen dasselbe -- die Zeigerkennung
       * und die Stelle. Was daraus wird, entscheidet erst das
       * Loslassen.
       */
      greift = naechster(x, y);
      zeigerId = e.pointerId;
      abX = x; abY = y;
      pointer.x = x;
      pointer.y = y;
      pointer.active = true;
      /* Gezogen wird nur an einem Punkt -- auf leerer Flaeche gibt es
         nichts zu ziehen, also auch keine Greifhand. */
      if (greift !== null) flaeche?.classList.add("login--zieht");
      if (reduced) draw();
    }

    /**
     * Einen Punkt setzen (EXTENSION §2).
     *
     * Er traegt dieselbe Form wie die automatischen: dieselbe langsame
     * Bewegung, derselbe Radiusbereich, dieselbe Zeichenroutine. Er ist
     * ein Netzwerkpunkt und kein Fremdkoerper -- und genau deshalb
     * wandert er auch mit, statt angepinnt zu stehen.
     *
     * ERKENNBAR, ABER NICHT AUFFAELLIG: er traegt das Markengruen, das
     * jeder zehnte automatische Punkt ohnehin traegt, und einen Radius
     * am oberen Ende des vorhandenen Bereichs. Wer ihn sucht, findet
     * ihn; wer sich anmelden will, sieht einen Punkt wie die anderen.
     */
    function setze(x: number, y: number): void {
      if (gesetzt >= MAX_MANUELL) return;
      particles.push({
        x, y,
        vx: (Math.random() - 0.5) * 0.11,
        vy: (Math.random() - 0.5) * 0.11,
        r: 1.7,
        accent: true,
        manuell: true,
      });
      gesetzt++;
    }

    function onUp(e: PointerEvent): void {
      if (e.pointerId !== zeigerId) return;
      const r = canvas!.getBoundingClientRect();
      const x = e.clientX - r.left, y = e.clientY - r.top;
      const gewandert = Math.hypot(x - abX, y - abY);

      if (greift !== null) {
        /*
         * AM PUNKT AUFGESETZT: daraus wird eine Verbindung oder
         * nichts. Ein Punkt entsteht hier NIE -- ein Zug darf nicht
         * zusaetzlich etwas setzen (EXTENSION §6).
         */
        const ziel = naechster(x, y);
        /*
         * Eine Verbindung entsteht nur ueber einem ANDEREN Punkt. Ueber
         * leerer Flaeche entsteht keine (§7 G) -- und auch nicht ueber
         * demselben Punkt, aus dem sie kommt (keine Selbstverbindung).
         *
         * Welcher der beiden Punkte automatisch entstanden ist und
         * welcher gesetzt wurde, spielt keine Rolle: verbunden werden
         * PUNKTE. Damit sind alle vier Kombinationen aus EXTENSION §3
         * ohne Sonderfall erlaubt.
         */
        if (ziel !== null && ziel !== greift) {
          const schon = links.some((l) =>
            (l.a === greift && l.b === ziel) || (l.a === ziel && l.b === greift));
          if (!schon) links.push({ a: greift, b: ziel });
        }
      } else if (gewandert < DRAG_SCHWELLE && naechster(x, y) === null) {
        /*
         * AUF LEERER FLAECHE GEKLICKT: ein neuer Punkt.
         *
         * Zwei Bedingungen, und beide sind noetig:
         *
         *   `gewandert < DRAG_SCHWELLE` -- wer ueber den Hintergrund
         *   zieht, wollte keinen Punkt setzen (§1, §6).
         *
         *   `naechster(x, y) === null` -- liegt doch ein Punkt in
         *   Greifweite, entsteht kein zweiter darueber (§6). Der Fall
         *   ist selten, weil dann schon beim Aufsetzen gegriffen
         *   worden waere; die Punkte wandern aber, und die Pruefung
         *   kostet nichts.
         */
        setze(x, y);
      }

      greift = null;
      zeigerId = -1;
      flaeche?.classList.remove("login--zieht");
      if (reduced) draw();
    }

    build();
    start();
    window.addEventListener("resize", onResize);
    document.addEventListener("visibilitychange", onVisibility);
    /*
     * SECHS HOERER, FEST GEZAEHLT (§4). Zwei fuer Groesse und
     * Sichtbarkeit, zwei fuer die Zeigerspur, zwei fuer das Ziehen.
     * Jeder wird im Aufraeumen wieder abgemeldet -- auch die, die nur
     * unter `!reduced` angemeldet wurden: `removeEventListener` auf
     * einen nie angemeldeten Hoerer ist ohne Wirkung und ohne Risiko.
     *
     * `pointermove` bleibt `passive` -- es scrollt hier nichts, und der
     * Zug braucht kein `preventDefault`. Die Punkte werden nicht
     * verschoben, nur verbunden; der Browser hat nichts zu verhindern.
     */
    if (!reduced) {
      window.addEventListener("pointermove", onPointer, { passive: true });
      window.addEventListener("pointerleave", onLeave);
    }
    /*
     * Das Greifen auch bei `prefers-reduced-motion`: die Animation
     * entfaellt dort, die Bedienbarkeit nicht. Gezeichnet wird dann
     * nach jedem Ereignis einmal.
     */
    if (reduced) {
      window.addEventListener("pointermove", onPointer, { passive: true });
      window.addEventListener("pointerleave", onLeave);
    }
    flaeche?.addEventListener("pointerdown", onDown);
    /* Auf `window` loslassen, nicht auf der Flaeche: wer ueber dem
       Formular loslaesst, soll den Zug trotzdem beenden -- sonst bliebe
       er haengen. */
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", onResize);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pointermove", onPointer);
      window.removeEventListener("pointerleave", onLeave);
      flaeche?.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      flaeche?.classList.remove("login--greifbar", "login--zieht");
    };
  }, []);

  // Rein dekorativ -- fuer Vorlesewerkzeuge unsichtbar.
  return <canvas ref={ref} className="particles" aria-hidden="true" />;
}
